import React from 'react'

import { iconIdMapping } from '@arcade/web/icons/react'

import {
  ArcadeAvatarBase,
  ArcadeAvatarBaseProps,
  avatarSizeDimensions,
  getAvatarSizeDimensions,
} from './ArcadeAvatarBase'
import { StackedAvatar } from './ArcadeStackedAvatar'

export {
  avatarSizeDimensions,
  DEFAULT_CARD_SCALE_FACTOR,
  DISABLED_OPACITY,
  getAvatarSizeDimensions,
  PrimaryAvatarWrapper,
  type AvatarSizeDimensions,
  type NamedSize,
  type Size,
} from './ArcadeAvatarBase'

/**
 * Prefer arcadeId over element.
 * Element is only used for custom icons.
 */
interface Icon {
  arcadeId?: string
  element?: React.ReactNode
}

export type StackedAvatarLayout = 'automatic' | 'horizontal'

export interface AvatarProps extends Omit<ArcadeAvatarBaseProps, 'icon' | 'overlayIcon' | 'overlayIconPadding'> {
  icon?: Icon
  overlayIcon?: Icon
  stackedAvatars?: AvatarProps[]
  stackedAvatarLayout?: StackedAvatarLayout
}

const getArcadeIconComponent = (icon: Icon) => {
  const arcadeIcon = icon?.arcadeId ? iconIdMapping[icon?.arcadeId] : undefined
  if (!arcadeIcon) {
    throw Error(`Avatar: No icon mapping found for id ${arcadeIcon}`)
  }
  return <arcadeIcon.Icon size={arcadeIcon.size} />
}

export const Avatar = ({
  icon,
  overlayIcon,
  stackedAvatars,
  stackedAvatarLayout,
  size = 'medium',
  disabled,
  ...props
}: AvatarProps) => {
  if (stackedAvatars) {
    return (
      <StackedAvatar stackedAvatars={stackedAvatars} size={size} disabled={disabled} layout={stackedAvatarLayout} />
    )
  }

  const { image, initial } = props
  const hasImage = image && (typeof image === 'string' || image.lightUrl)
  const primaryIcon = !hasImage && !initial && icon?.arcadeId ? getArcadeIconComponent(icon) : icon?.element
  const overlayVisible = getAvatarSizeDimensions(size).width >= avatarSizeDimensions.xsmall.width
  const resolvedOverlayIcon =
    overlayVisible && overlayIcon?.arcadeId ? getArcadeIconComponent(overlayIcon) : overlayIcon?.element

  return (
    <ArcadeAvatarBase
      {...props}
      size={size}
      disabled={disabled}
      icon={primaryIcon}
      overlayIcon={resolvedOverlayIcon}
      overlayIconPadding={!!overlayIcon?.arcadeId}
    />
  )
}

Avatar.displayName = 'ArcadeAvatar'
